import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const PosKeyboardContext = createContext(null);

export const usePosKeyboard = () => useContext(PosKeyboardContext);

const POS_SKIP_TYPES = ['section', 'page'];

const PosKeyboardProvider = ({ children }) => {
  const [activeField, setActiveField] = useState(null);
  const dismissTimerRef = useRef(null);
  const skipNextRef = useRef(false);

  const registerField = useCallback((field, value, onChange, ref, element) => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    const fieldKey = field?.value || field?.label;
    if (POS_SKIP_TYPES.includes(field?.typeData)) {
      skipNextRef.current = true;
      setActiveField({ field, value, onChange, ref, fieldKey, element });
      return;
    }
    setActiveField({ field, value, onChange, ref, fieldKey, element });
  }, []);

  const updateValue = useCallback((newValue) => {
    setActiveField((prev) => (prev ? { ...prev, value: newValue } : null));
  }, []);

  const unregisterField = useCallback(() => {
    dismissTimerRef.current = setTimeout(() => {
      setActiveField(null);
      dismissTimerRef.current = null;
    }, 150);
  }, []);

  const dismiss = useCallback(() => {
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    setActiveField(null);
  }, []);

  const goToNextField = useCallback(() => {
    if (!activeField?.fieldKey) return false;
    const allFields = Array.from(document.querySelectorAll('[data-pos-field]'));
    if (allFields.length < 2) return false;
    const currentIdx = allFields.findIndex((el) => el.getAttribute('data-pos-field') === activeField.fieldKey);
    if (currentIdx < 0 || currentIdx >= allFields.length - 1) return false;
    if (document.activeElement) document.activeElement.blur();
    const nextEl = allFields[currentIdx + 1];
    nextEl.click();
    const nextInput = nextEl.querySelector('input, textarea');
    if (nextInput) nextInput.focus();
    return true;
  }, [activeField]);

  useEffect(() => {
    if (skipNextRef.current && activeField) {
      skipNextRef.current = false;
      setTimeout(() => goToNextField(), 50);
    }
  }, [activeField, goToNextField]);

  const value = {
    activeField,
    isKeyboardOpen: activeField !== null,
    registerField,
    unregisterField,
    updateValue,
    dismiss,
    goToNextField,
  };

  return (
    <PosKeyboardContext.Provider value={value}>
      {children}
    </PosKeyboardContext.Provider>
  );
};

export default PosKeyboardProvider;
